import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Bell, Loader2, Mail, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { getCredits, refreshCredits, sendCreditsTestEmail } from "@/api/credits";
import { formatDate, formatRelativeDate } from "@/lib/formatters";
import { cn } from "@/lib/utils";
import type { CreditLevel, Project, ProjectCredits, ProviderCredit } from "@/api/types";

interface CreditsSectionProps {
  project: Project;
  canCheck: boolean;
  canSendTest: boolean;
}

const LEVELS: Record<CreditLevel, { label: string; pill: string; dot: string; ring: string }> = {
  ok: { label: "OK", pill: "bg-emerald-50 text-emerald-700", dot: "bg-emerald-500", ring: "#4f46e5" },
  low: { label: "Running low", pill: "bg-amber-50 text-amber-800", dot: "bg-amber-500", ring: "#f59e0b" },
  capped: { label: "Monthly cap reached", pill: "bg-orange-50 text-orange-800", dot: "bg-orange-500", ring: "#f97316" },
  empty: { label: "Empty", pill: "bg-rose-50 text-rose-700", dot: "bg-rose-500", ring: "#f43f5e" },
  unknown: { label: "Unknown", pill: "bg-slate-100 text-slate-600", dot: "bg-slate-400", ring: "#94a3b8" },
};

// The order the tab shows the accounts in, whatever order the check stored them.
const ORDER = ["apollo", "theirstack", "enrichlayer", "flyby", "lusha", "kaspr"];
// What each account brings, under its name; and its monogram.
const ROLES: Record<string, string> = {
  apollo: "People and professional emails",
  theirstack: "Job postings",
  enrichlayer: "Founder careers",
  flyby: "Crunchbase companies and founders",
  lusha: "Phone numbers",
  kaspr: "Phones and emails",
};
const MONOGRAMS: Record<string, string> = {
  apollo: "AP",
  theirstack: "TS",
  enrichlayer: "EL",
  flyby: "FB",
  lusha: "LU",
  kaspr: "KA",
};
const DETAIL_ORDER = ["used", "work emails", "personal emails", "exports"];

function toNumber(value?: string | null): number | null {
  const n = value == null ? Number.NaN : Number(value);
  return Number.isFinite(n) ? n : null;
}

function amount(value: number | null): string {
  return value == null ? "—" : value.toLocaleString("en-US", { maximumFractionDigits: 2 });
}

function extractMessage(err: unknown): string {
  if (axios.isAxiosError(err)) {
    if (err.code === "ERR_NETWORK") return "Cannot reach the server.";
    const detail = (err.response?.data as { detail?: unknown } | undefined)?.detail;
    if (typeof detail === "string") return detail;
  }
  return "An unexpected error occurred.";
}

/** Settings → Credits: every provider account the project pays with, after the owner's
 *  nidria front (2026-09-29): white planes, a ring of what is left of the plan, the
 *  details as rows and the alert threshold in the footer. */
export function CreditsSection({ project, canCheck, canSendTest }: CreditsSectionProps) {
  const [credits, setCredits] = useState<ProjectCredits | null>(null);
  const [loading, setLoading] = useState(true);
  const [checking, setChecking] = useState(false);
  const [testing, setTesting] = useState(false);

  const load = useCallback(async () => {
    try {
      setCredits(await getCredits(project.slug));
    } catch (err) {
      toast.error(extractMessage(err));
    } finally {
      setLoading(false);
    }
  }, [project.slug]);

  useEffect(() => {
    void load();
  }, [load]);

  const check = async () => {
    setChecking(true);
    try {
      setCredits(await refreshCredits(project.slug));
      toast.success("Credits checked.");
    } catch (err) {
      toast.error(extractMessage(err));
    } finally {
      setChecking(false);
    }
  };

  const sendTest = async () => {
    setTesting(true);
    try {
      const result = await sendCreditsTestEmail(project.slug);
      if (result.sent_to.length) toast.success(`Test email sent to ${result.sent_to.join(", ")}.`);
      if (result.failed?.length) toast.error(`Not sent to ${result.failed.join(", ")}.`);
    } catch (err) {
      toast.error(extractMessage(err));
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
      </div>
    );
  }

  if (!credits?.configured) {
    return (
      <div className="surface-plane p-6 text-sm text-slate-500">
        The provider credits check is not set up for this project.
      </div>
    );
  }

  const providers = [...(credits.providers ?? [])].sort(
    (a, b) => ORDER.indexOf(a.provider) - ORDER.indexOf(b.provider),
  );
  const recipients = credits.recipients ?? [];
  const counts = providers.reduce<Record<string, number>>((acc, item) => {
    acc[item.level] = (acc[item.level] ?? 0) + 1;
    return acc;
  }, {});
  const schedule =
    credits.schedule === "*/30 * * * *" ? "every 30 minutes" : (credits.schedule ?? "not scheduled");

  return (
    <div className="space-y-5">
      <section className="surface-plane flex flex-wrap items-center justify-between gap-5 p-5 sm:p-6">
        <div className="min-w-0 space-y-2.5">
          <h3 className="type-zonehead">Provider credits</h3>
          <p className="text-sm text-slate-700">
            {credits.checked_at
              ? `Checked ${formatRelativeDate(credits.checked_at)}`
              : "Not checked yet"}
            <span className="text-slate-400">
              {" "}· {schedule} · reading a balance spends no credit
            </span>
          </p>
          <div className="flex flex-wrap items-center gap-2">
            {(Object.keys(LEVELS) as CreditLevel[])
              .filter((level) => counts[level])
              .map((level) => (
                <span
                  key={level}
                  className={cn(
                    "inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium",
                    LEVELS[level].pill,
                  )}
                >
                  <span className={cn("h-1.5 w-1.5 rounded-full", LEVELS[level].dot)} />
                  {counts[level]} {LEVELS[level].label.toLowerCase()}
                </span>
              ))}
            <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-2.5 py-1 text-xs text-slate-600">
              <Mail className="h-3 w-3" />
              {recipients.length ? `Alerts to ${recipients.join(", ")}` : "No alert recipient"}
            </span>
          </div>
          {credits.last_run_status === "failed" && credits.last_run_error && (
            <p className="text-xs text-rose-600">Last check failed: {credits.last_run_error}</p>
          )}
        </div>
        <div className="flex shrink-0 gap-2">
          {canSendTest && recipients.length > 0 && (
            <button
              type="button"
              onClick={sendTest}
              disabled={testing || providers.length === 0}
              className="inline-flex h-9 items-center gap-2 rounded-lg bg-indigo-500/[0.08] px-3.5 text-sm font-medium text-indigo-700 transition-colors hover:bg-indigo-500/[0.14] disabled:opacity-50"
            >
              {testing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
              Send test email
            </button>
          )}
          {canCheck && (
            <button
              type="button"
              onClick={check}
              disabled={checking}
              className="shadow-cta inline-flex h-9 items-center gap-2 rounded-lg bg-indigo-600 px-3.5 text-sm font-medium text-white transition-colors hover:bg-indigo-700 disabled:opacity-60"
            >
              <RefreshCw className={cn("h-4 w-4", checking && "animate-spin")} />
              Check now
            </button>
          )}
        </div>
      </section>

      {providers.length === 0 ? (
        <div className="surface-plane p-6 text-sm text-slate-500">No check has completed yet.</div>
      ) : (
        <div className="grid items-start gap-5 md:grid-cols-2">
          {providers.map((item) => (
            <ProviderCard key={item.provider} item={item} />
          ))}
        </div>
      )}
    </div>
  );
}

function ProviderCard({ item }: { item: ProviderCredit }) {
  const level = LEVELS[item.level] ?? LEVELS.unknown;
  const remaining = toNumber(item.remaining);
  const limit = toNumber(item.limit);
  const month = item.month ?? {};
  const spent = toNumber(month.spent);
  const cap = toNumber(month.cap_cost);
  const share = remaining != null && limit ? Math.max(0, Math.min(1, remaining / limit)) : null;

  const rows: [string, string][] = [];
  if (limit != null) rows.push(["Plan", `${amount(limit)} ${item.unit}`]);
  const details = Object.entries(item.details ?? {}).sort(
    ([a], [b]) => DETAIL_ORDER.indexOf(a) - DETAIL_ORDER.indexOf(b),
  );
  for (const [name, value] of details) {
    rows.push([name.charAt(0).toUpperCase() + name.slice(1), amount(toNumber(value))]);
  }
  if (item.expires_on) rows.push(["Expires", formatDate(item.expires_on)]);

  let note: string | null = null;
  if (item.error === "NO_ANSWER_YET") note = "Known after its first call: the provider states it in each answer.";
  else if (item.error === "NO_KEY") note = "No API key for this account on the server.";
  else if (item.error?.startsWith("HTTP_40"))
    note = `The provider refused the key (${item.error.replace("_", " ")}).`;
  else if (item.error) note = `Balance unavailable (${item.error}).`;
  else if (item.source === "last_answer" && item.observed_at)
    note = `As stated by its last answer, ${formatRelativeDate(item.observed_at)}.`;

  return (
    <section className="surface-plane p-5">
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-indigo-500/[0.08] text-[13px] font-semibold text-indigo-700">
            {MONOGRAMS[item.provider] ?? item.label.slice(0, 2).toUpperCase()}
          </span>
          <div className="min-w-0">
            <h4 className="truncate text-[15px] font-semibold text-slate-900">{item.label}</h4>
            <p className="truncate text-[12.5px] text-slate-500">{ROLES[item.provider] ?? item.unit}</p>
          </div>
        </div>
        <span
          className={cn(
            "inline-flex shrink-0 items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold",
            level.pill,
          )}
        >
          <span className={cn("h-1.5 w-1.5 rounded-full", level.dot)} />
          {level.label}
        </span>
      </div>

      <div className="mt-5 flex items-center justify-between gap-4">
        <div>
          <p className="text-[32px] font-medium leading-none tabular-nums text-slate-900">
            {amount(remaining)}
          </p>
          <p className="mt-1.5 text-[13px] text-slate-500">{item.unit} left</p>
        </div>
        {share != null && <Ring share={share} color={level.ring} />}
      </div>

      {note && (
        <p className="mt-4 rounded-lg bg-slate-50 px-3 py-2 text-[12.5px] text-slate-600">{note}</p>
      )}

      {rows.length > 0 && (
        <dl className="mt-4 divide-y divide-slate-900/[0.05] border-t border-slate-900/[0.05]">
          {rows.map(([name, value]) => (
            <div key={name} className="flex items-center justify-between py-2 text-[13px]">
              <dt className="text-slate-500">{name}</dt>
              <dd className="font-medium tabular-nums text-slate-800">{value}</dd>
            </div>
          ))}
        </dl>
      )}

      {cap != null && (
        <div className="mt-4 rounded-xl bg-slate-50/80 p-3.5">
          <div className="flex items-center justify-between text-[12.5px]">
            <span className="font-medium text-slate-600">This month</span>
            <span className="tabular-nums text-slate-500">
              {amount(spent)} of {amount(cap)} · {month.calls ?? 0} calls
            </span>
          </div>
          <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-indigo-100/70">
            <div
              className={cn("h-full rounded-full", month.capped ? "bg-orange-500" : "bg-indigo-500")}
              style={{ width: `${Math.min(100, ((spent ?? 0) / cap) * 100)}%` }}
            />
          </div>
          {month.capped && (
            <p className="mt-2 text-[12px] text-orange-700">
              The engine stops calling it until the cap is raised or the month renews.
            </p>
          )}
        </div>
      )}
      {month.error && (
        <p className="mt-4 text-[12.5px] text-slate-500">
          No monthly budget is running for this account.
        </p>
      )}

      <p className="flex items-center gap-1.5 pt-4 text-[12px] text-slate-400">
        <Bell className="h-3.5 w-3.5" />
        Alert below {amount(item.threshold)} {item.unit}
      </p>
    </section>
  );
}

/** What is left of the plan, as a ring with its share in the middle. */
function Ring({ share, color }: { share: number; color: string }) {
  const r = 26;
  const length = 2 * Math.PI * r;
  return (
    <div className="relative h-16 w-16 shrink-0">
      <svg viewBox="0 0 64 64" className="h-16 w-16 -rotate-90">
        <circle cx="32" cy="32" r={r} fill="none" stroke="#eef2ff" strokeWidth="6" />
        <circle
          cx="32"
          cy="32"
          r={r}
          fill="none"
          stroke={color}
          strokeWidth="6"
          strokeLinecap="round"
          strokeDasharray={`${length * share} ${length}`}
        />
      </svg>
      <span className="absolute inset-0 flex items-center justify-center text-[12px] font-semibold tabular-nums text-slate-700">
        {Math.round(share * 100)}%
      </span>
    </div>
  );
}
